import React, { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container } from "reactstrap";
import { ArrowLeft, Upload } from "react-feather";
import Breadcrumb from "../../common/breadcrumb";
import Loader from "../../../shared/Loader";
import { errorToast } from "../../../shared/Toast";
import ToggleSwitch from "../../../shared/ToggleSwitch";
import placeholder from "../../../assets/images/placeholder.png";
import { getMenuById, updateMenu } from "../../../utils/apiCalls";
import { canManageMenus } from "../../../constants/roles";
import { getProductImageUrl } from "../../../utils/imageUtils";
import { C, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { RCard, SectionTitle } from "../../reporting/ReportingShared";
import MenuHiddenProducts, { groupHiddenRows } from "./menu-hidden-products";

const STATUS_TOGGLES = [
  { key: "active", title: "Active", hint: "The branch's live menu. Activating it deactivates the branch's other menus." },
  { key: "disable_menu_ordering", title: "Disable Menu Ordering", hint: "Customers can browse but not order" },
  { key: "all_category_visibility", title: "All Category Visibility", hint: "Show every category, even empty ones" },
  { key: "requires_table", title: "Requires Table", hint: "Ask for a table number before ordering" },
];

const allergenImageUrl = (menu) => {
  if (menu.allergen_image) return process.env.REACT_APP_URL + menu.allergen_image;
  if (menu.allergen_merchant_image) {
    return `${process.env.REACT_APP_URL}api/proxy/image?url=${encodeURIComponent(menu.allergen_merchant_image)}`;
  }
  return placeholder;
};

// One image slot: preview plus a file picker; `onPick` gets the chosen File.
const ImageField = ({ label, hint, src, onPick }) => {
  const inputRef = useRef(null);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <img
        src={src}
        alt=""
        style={{ width: 72, height: 72, borderRadius: 12, objectFit: "cover", border: `1px solid ${C.border}`, background: C.bg }}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{label}</div>
        <div style={{ fontSize: 11, color: C.textLight, marginBottom: 8 }}>{hint}</div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          style={{
            background: C.white,
            border: `1.5px solid ${C.border}`,
            borderRadius: 8,
            padding: "6px 12px",
            fontWeight: 600,
            fontSize: 12,
            color: C.text,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Upload size={12} /> Change
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onPick(file);
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
};

const EditProductMenu = () => {
  const navigate = useNavigate();
  const { menuId } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // The hidden products are separate records with their own immediate saves, so they get their
  // own tab rather than sitting under this form's Save button.
  // Branch staff (waitlist_staff) may only mark products unavailable, so they get that tab alone.
  const detailsEditable = canManageMenus();
  const [activeSection, setActiveSection] = useState(detailsEditable ? "details" : "hidden");
  const [hiddenCount, setHiddenCount] = useState(0);
  const [menu, setMenu] = useState(null);
  const [form, setForm] = useState({
    title: "",
    active: true,
    disable_menu_ordering: false,
    all_category_visibility: false,
    requires_table: false,
  });
  const [menuImage, setMenuImage] = useState({ file: null, preview: placeholder });
  const [allergenImage, setAllergenImage] = useState({ file: null, preview: placeholder });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = await getMenuById(menuId);
      if (data) {
        setMenu(data);
        setForm({
          title: data.title || "",
          active: !!data.active,
          disable_menu_ordering: !!data.disable_menu_ordering,
          all_category_visibility: !!data.all_category_visibility,
          requires_table: !!data.requires_table,
        });
        setMenuImage({ file: null, preview: getProductImageUrl(data) || placeholder });
        setAllergenImage({ file: null, preview: allergenImageUrl(data) });
        setHiddenCount(groupHiddenRows(data.hiddenProducts).length);
      }
      setLoading(false);
    };
    load();
  }, [menuId]);

  const pickImage = (setter) => (file) => {
    const reader = new FileReader();
    reader.onload = () => setter({ file, preview: reader.result });
    reader.readAsDataURL(file);
  };

  const handleDiscard = () => navigate("/products/product-menu-list");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      errorToast("Menu name is required.");
      return;
    }

    const formData = new FormData();
    formData.append("dataType", "menu");
    formData.append("title", form.title.trim());
    formData.append("branchId", menu.branchId);
    formData.append("merchantId", menu.MerchantId);
    formData.append("active", form.active);
    formData.append("disable_menu_ordering", form.disable_menu_ordering);
    formData.append("all_category_visibility", form.all_category_visibility);
    formData.append("requires_table", form.requires_table);
    if (menuImage.file instanceof File) formData.append("image", menuImage.file);
    if (allergenImage.file instanceof File) formData.append("allergen_image", allergenImage.file);

    setSaving(true);
    const result = await updateMenu(menuId, formData);
    setSaving(false);
    if (result) navigate("/products/product-menu-list");
  };

  const inputStyle = {
    padding: "10px 12px",
    fontSize: 13,
    borderRadius: 8,
    border: `1.5px solid ${C.border}`,
    fontFamily: "inherit",
    outline: "none",
    color: C.text,
    background: C.white,
    width: "100%",
  };
  const readOnlyInputStyle = { ...inputStyle, background: C.bg, color: C.textMed, cursor: "not-allowed" };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 6, display: "block" };
  const fieldHintStyle = { fontSize: 11, color: C.textLight, marginTop: 4 };
  const primaryBtnStyle = {
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
    border: "none",
    borderRadius: 8,
    padding: "10px 24px",
    fontWeight: 600,
    color: C.white,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
    cursor: saving ? "default" : "pointer",
    opacity: saving ? 0.7 : 1,
    fontSize: 13,
  };
  const ghostBtnStyle = {
    background: C.white,
    border: `1.5px solid ${C.border}`,
    borderRadius: 8,
    padding: "10px 24px",
    fontWeight: 600,
    color: C.text,
    cursor: "pointer",
    fontSize: 13,
  };
  const toggleRowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: C.white,
  };

  return (
    <Fragment>
      <Breadcrumb title="Edit Menu" parent="Products" />
      <Container fluid>
        <style>
          {reportingGlobalCSS}
          {`.menu-edit-layout { display: grid; grid-template-columns: 340px minmax(0, 1fr); gap: 20px; align-items: start; }
            @media (max-width: 991px) { .menu-edit-layout { grid-template-columns: minmax(0, 1fr); } }`}
        </style>
        <div className="rpt-wrap">
          {loading ? (
            <Loader />
          ) : !menu ? (
            <RCard>
              <div style={{ textAlign: "center", padding: 30, color: C.textMed }}>
                Menu not found.{" "}
                <button type="button" onClick={handleDiscard} style={{ ...ghostBtnStyle, marginLeft: 8 }}>
                  Back to menus
                </button>
              </div>
            </RCard>
          ) : (
            <>
              {/* Heading */}
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                <div
                  onClick={handleDiscard}
                  title="Back"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    border: `1.5px solid ${C.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    background: C.white,
                    flexShrink: 0,
                  }}
                >
                  <ArrowLeft size={16} color={C.textMed} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.text, letterSpacing: -0.3 }}>Edit Menu</div>
                  <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>
                    {menu.title}
                    {menu.Branch?.name ? ` · ${menu.Branch.name}` : ""}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: `1px solid ${C.border}`, overflowX: "auto" }}>
                {[
                  ...(detailsEditable ? [{ key: "details", label: "Menu Details" }] : []),
                  { key: "hidden", label: `Unavailable Products (${hiddenCount})` },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setActiveSection(t.key)}
                    style={{
                      background: "none",
                      border: "none",
                      borderBottom: `2px solid ${activeSection === t.key ? C.coral : "transparent"}`,
                      color: activeSection === t.key ? C.coral : C.textMed,
                      fontWeight: activeSection === t.key ? 700 : 500,
                      fontSize: 13,
                      padding: "10px 16px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {activeSection === "hidden" || !detailsEditable ? (
                <MenuHiddenProducts menuId={menu.id} merchantId={menu.MerchantId} onCountChange={setHiddenCount} />
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="menu-edit-layout">
                    {/* LEFT: images */}
                    <div>
                      <RCard>
                        <SectionTitle>Images</SectionTitle>
                        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                          <ImageField
                            label="Menu image"
                            hint="Shown at the top of the menu"
                            src={menuImage.preview}
                            onPick={pickImage(setMenuImage)}
                          />
                          <ImageField
                            label="Allergen image"
                            hint="Allergen chart for this menu"
                            src={allergenImage.preview}
                            onPick={pickImage(setAllergenImage)}
                          />
                        </div>
                      </RCard>
                    </div>

                    {/* RIGHT: details + status */}
                    <div>
                      <RCard>
                        <SectionTitle>Basic Info</SectionTitle>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
                            gap: 16,
                          }}
                        >
                          <div>
                            <label style={labelStyle}>Name *</label>
                            <input
                              type="text"
                              required
                              value={form.title}
                              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                              style={inputStyle}
                            />
                          </div>
                          <div>
                            <label style={labelStyle}>Branch</label>
                            <input type="text" value={menu.Branch?.name || `#${menu.branchId}`} readOnly style={readOnlyInputStyle} />
                            <div style={fieldHintStyle}>A menu stays on the branch it was created for.</div>
                          </div>
                        </div>
                      </RCard>

                      <RCard>
                        <SectionTitle>Status</SectionTitle>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
                            gap: 12,
                          }}
                        >
                          {STATUS_TOGGLES.map((t) => (
                            <div key={t.key} style={toggleRowStyle}>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{t.title}</div>
                                <div style={{ fontSize: 11, color: C.textLight }}>{t.hint}</div>
                              </div>
                              <ToggleSwitch
                                checked={form[t.key]}
                                handleToggle={(isChecked) => setForm((prev) => ({ ...prev, [t.key]: isChecked }))}
                              />
                            </div>
                          ))}
                        </div>
                      </RCard>

                      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginBottom: 20 }}>
                        <button type="button" onClick={handleDiscard} style={ghostBtnStyle}>
                          Discard
                        </button>
                        <button type="submit" style={primaryBtnStyle} disabled={saving}>
                          {saving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </Container>
    </Fragment>
  );
};

export default EditProductMenu;
